/**
 * Main Application Component
 *
 * Sets up routing, authentication context, consent management, and the main layout.
 * Supports both Azure B2C and local JWT authentication modes.
 * Includes responsive navigation with mobile hamburger menu.
 * Includes cookie consent banner and preferences modal for GDPR/CCPA compliance.
 */

import { useState, useEffect, Suspense, lazy, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  MantineProvider,
  AppShell,
  NavLink,
  Button,
  Group,
  Loader,
  Center,
  Burger,
  Title,
  Divider,
  Text,
  UnstyledButton,
  Box,
  Stack,
  Badge,
  rem,
  Menu,
  ScrollArea,
} from '@mantine/core';
import { appTheme, appCssVariablesResolver, brandColors, semanticColors, logoSizes } from './config/theme';
import { Notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
// Charts CSS lives with admin chart components (PBI #1138) — not public bootstrap.
// Core styles.css is imported once via mantineEntryStyles in main.tsx (PBI #1144).
import './styles/mantineNotificationsStyles';
import {
  IconHome,
  IconUsers,
  IconCalendarEvent,
  IconCalendar,
  IconMailFast,
  IconMail,
  IconLogout,
  IconShield,
  IconFlower,
  IconMailbox,
  IconLifebuoy,
  IconReceipt,
} from '@tabler/icons-react';
import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthProvider';
import { useConsent } from './contexts/ConsentContext';
import { ConsentProviderWithModal } from './contexts/ConsentProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { UnauthenticatedCatchAll } from './components/UnauthenticatedCatchAll';
import { ScrollToTop } from './components/common/ScrollToTop';
import {
  AUTH_ROUTES,
  hasReturnTo,
  getAndClearReturnTo,
  isPublicPath,
  shouldStayOnPublicLanding,
  VIEW_PUBLIC_LANDING_STATE_KEY,
} from './constants/auth';
import {
  LANDING_EXPLORE_SECTION_ID,
  OVERVIEW_SEND_A_SMILE_SECTION_ID,
} from './constants/landingExplore';
import { APP_ROUTES, PUBLIC_ROUTES } from './constants/routes';
// Landing stays eager so `/` LCP is not delayed by an extra route chunk (PBI #1137).
import { LandingPage } from './pages/LandingPage';
import {
  LegalModal,
  type LegalModalType,
  CookiePreferencesModal,
  UserAccountMenu,
  AppRootCookieBanner,
  DeferredPublicCookieBanner,
} from './components/common';
import { SubscriptionProvider } from './contexts/SubscriptionProvider';
import { UpgradeModalProvider } from './contexts/UpgradeModalProvider';
import { PaymentMethodModalProvider } from './contexts/PaymentMethodModalProvider';
import { FeaturesProvider } from './contexts/FeaturesProvider';
import { useFeatures } from './contexts/FeaturesContext';
import { shouldSuppressAppShellForOnboarding } from './components/subscription/welcomeOnboardingGate';
import { useSubscription } from './contexts/SubscriptionContext';
import { CONSENT_UI_TEXT } from './constants/consent';
import { trackPageView, updateConsentSignals } from './services/marketingTracking';
import {
  captureAcquisitionAttributionFromLocation,
  syncAcquisitionAttributionWithConsent,
} from './services/acquisitionAttributionService';
import { AdminAppShell, AdminRoute } from './components/admin';
import portalSwitchMenuItemClasses from './components/common/PortalSwitchMenuItem.module.css';
import { hasGuestDraft, loadGuestDraft, type GuestSchedulingDraft } from './services/guestDraftService';
import { hasGuestFlowerDraft, loadGuestFlowerDraft, type GuestFlowerDraft } from './services/guestDraftService';
import { hasGuestMailedCardDraft, loadGuestMailedCardDraft, type GuestMailedCardDraft } from './services/guestDraftService';
import { usePasswordManagerSuppressor } from './hooks/usePasswordManagerSuppressor';
import { useMySupportUnreadCount } from './hooks/useSupportUnreadCount';
import { useUnopenedEcardWarningCount } from './hooks/useUnopenedEcardWarningCount';
import { getMySupportUnreadCount } from './services/supportTicketsService';
import { PlaceholderProvider } from './contexts/PlaceholderProvider';
import { GiftShopRoute } from './components/routing/GiftShopRoute';

/** Compact Suspense fallback for lazy public / auth route chunks. */
const RouteChunkFallback = () => (
  <Center style={{ height: '50vh' }}><Loader size="lg" /></Center>
);

const PhoneE2eHarnessPage =
  import.meta.env.MODE === 'e2e'
    ? lazy(() => import('./pages/e2e/PhoneE2eHarnessPage'))
    : null;

// Lazy public / marketing pages (LandingPage is eager — see import above). PBI #1137.
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then(m => ({ default: m.FaqPage })));
const GuidesIndexPage = lazy(() =>
  import('./pages/GuidesIndexPage').then(m => ({ default: m.GuidesIndexPage })),
);
const AutomatedOccasionGiftsPage = lazy(() =>
  import('./pages/AutomatedOccasionGiftsPage').then(m => ({ default: m.AutomatedOccasionGiftsPage })),
);
const PublicNotFoundPage = lazy(() =>
  import('./pages/PublicNotFoundPage').then(m => ({ default: m.PublicNotFoundPage })),
);
const PublicAiPage = lazy(() =>
  import('./pages/PublicAiPage').then(m => ({ default: m.PublicAiPage })),
);
const AiUseCaseDocPage = lazy(() =>
  import('./pages/AiUseCaseDocPage').then(m => ({ default: m.AiUseCaseDocPage })),
);
const CardsPage = lazy(() => import('./pages/CardsPage').then(m => ({ default: m.CardsPage })));
const PublicFlowersPage = lazy(() => import('./pages/PublicFlowersPage').then(m => ({ default: m.PublicFlowersPage })));
const PublicFlowerProductPage = lazy(() => import('./pages/PublicFlowerProductPage').then(m => ({ default: m.PublicFlowerProductPage })));
const PublicMailedCardsPage = lazy(() => import('./pages/PublicMailedCardsPage').then(m => ({ default: m.PublicMailedCardsPage })));
const PublicMailedCardProductPage = lazy(() => import('./pages/PublicMailedCardProductPage').then(m => ({ default: m.PublicMailedCardProductPage })));
const PublicGiftsPage = lazy(() => import('./pages/PublicGiftsPage').then(m => ({ default: m.PublicGiftsPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const IntroOfferPage = lazy(() => import('./pages/IntroOfferPage').then(m => ({ default: m.IntroOfferPage })));
const NeverMissPage = lazy(() => import('./pages/NeverMissPage').then(m => ({ default: m.NeverMissPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const ReminderConfirmPage = lazy(() => import('./pages/ReminderConfirmPage').then(m => ({ default: m.ReminderConfirmPage })));
const ReminderUnsubscribePage = lazy(() => import('./pages/ReminderUnsubscribePage').then(m => ({ default: m.ReminderUnsubscribePage })));
const MarketingUnsubscribePage = lazy(() => import('./pages/MarketingUnsubscribePage').then(m => ({ default: m.MarketingUnsubscribePage })));
const EmailWhitelistHelpPage = lazy(() => import('./pages/EmailWhitelistHelpPage').then(m => ({ default: m.EmailWhitelistHelpPage })));
const CardViewPage = lazy(() => import('./pages/CardViewPage').then(m => ({ default: m.CardViewPage })));
const CardPreviewPage = lazy(() => import('./pages/CardPreviewPage').then(m => ({ default: m.CardPreviewPage })));
const PublicEcardProductPage = lazy(() => import('./pages/PublicEcardProductPage').then(m => ({ default: m.PublicEcardProductPage })));
const AgentEcardReviewPage = lazy(() => import('./pages/AgentEcardReviewPage').then(m => ({ default: m.AgentEcardReviewPage })));
const AgentContactChangeReviewPage = lazy(() =>
  import('./pages/AgentContactChangeReviewPage').then(m => ({ default: m.AgentContactChangeReviewPage })));
const AgentOrderReviewPage = lazy(() =>
  import('./pages/AgentOrderReviewPage').then(m => ({ default: m.AgentOrderReviewPage })));
const AcceptTermsPage = lazy(() => import('./pages/AcceptTermsPage').then(m => ({ default: m.AcceptTermsPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const AuthStartPage = lazy(() => import('./pages/AuthStartPage').then(m => ({ default: m.AuthStartPage })));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));

// Lazy onboarding / guest-draft modals — keep Stripe and order wizards off browse entry.
const WelcomeOnboarding = lazy(() =>
  import('./components/subscription/WelcomeOnboarding').then(m => ({ default: m.WelcomeOnboarding })),
);
const ContactSelectionModal = lazy(() =>
  import('./components/subscription/ContactSelectionModal').then(m => ({ default: m.ContactSelectionModal })),
);
const ReviewDraftModal = lazy(() =>
  import('./components/contacts/ReviewDraftModal').then(m => ({ default: m.ReviewDraftModal })),
);
const ReviewFlowerDraftModal = lazy(() =>
  import('./components/flowers/ReviewFlowerDraftModal').then(m => ({ default: m.ReviewFlowerDraftModal })),
);
const ReviewMailedCardDraftModal = lazy(() =>
  import('./components/mailedCards').then(m => ({ default: m.ReviewMailedCardDraftModal })),
);

// Lazy-loaded authenticated pages -- reduces initial bundle parse time so
// auth init can start sooner on every page.goto() reload in E2E tests.
// Uses .then() adapter because these pages use named exports, not default.
// Lazy-loaded authenticated pages -- reduces initial bundle parse time so
// auth init can start sooner on every page.goto() reload in E2E tests.
// Uses .then() adapter because these pages use named exports, not default.
const AppNotFoundPage = lazy(() =>
  import('./pages/AppNotFoundPage').then(m => ({ default: m.AppNotFoundPage })),
);
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ContactsPage = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const EventTypesPage = lazy(() => import('./pages/EventTypesPage').then(m => ({ default: m.EventTypesPage })));
const ScheduledItemsPage = lazy(() => import('./pages/ScheduledItemsPage').then(m => ({ default: m.ScheduledItemsPage })));
const UpcomingEventsPage = lazy(() => import('./pages/UpcomingEventsPage').then(m => ({ default: m.UpcomingEventsPage })));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then(m => ({ default: m.OrdersPage })));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage').then(m => ({ default: m.MyAccountPage })));
const ConnectedAssistantsPage = lazy(() => import('./pages/ConnectedAssistantsPage').then(m => ({ default: m.ConnectedAssistantsPage })));
const FlowersPage = lazy(() => import('./pages/FlowersPage').then(m => ({ default: m.FlowersPage })));
const MailedCardsPage = lazy(() => import('./pages/MailedCardsPage').then(m => ({ default: m.MailedCardsPage })));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage').then(m => ({ default: m.ContactUsPage })));
const ContactUsThreadPage = lazy(() => import('./pages/ContactUsThreadPage').then(m => ({ default: m.ContactUsThreadPage })));

// Lazy-loaded admin pages for code splitting
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminOperationsPage = lazy(() => import('./pages/admin/AdminOperationsPage'));
const AdminAuditEventsPage = lazy(() => import('./pages/admin/AdminAuditEventsPage'));
const AdminTemplatesPage = lazy(() => import('./pages/admin/AdminTemplatesPage'));
const AdminEventTypesPage = lazy(() => import('./pages/admin/AdminEventTypesPage'));
const AdminContactRelationshipsPage = lazy(() => import('./pages/admin/AdminContactRelationshipsPage'));
const AdminSiteSettingsPage = lazy(() => import('./pages/admin/AdminSiteSettingsPage'));
const AdminPlaceholdersPage = lazy(() => import('./pages/admin/AdminPlaceholdersPage'));
const AdminAiModelsPage = lazy(() => import('./pages/admin/AdminAiModelsPage'));
const AdminContentGenerationPage = lazy(() => import('./pages/admin/AdminContentGenerationPage'));
const AdminCustomersPage = lazy(() => import('./pages/admin/AdminCustomersPage'));
const AdminFlowerCategoriesPage = lazy(() => import('./pages/admin/AdminFlowerCategoriesPage'));
const AdminFlowerVendorsPage = lazy(() => import('./pages/admin/AdminFlowerVendorsPage'));
const AdminFlowerPromotionsPage = lazy(() => import('./pages/admin/AdminFlowerPromotionsPage'));
const AdminMailedCardArtworkPage = lazy(() => import('./pages/admin/AdminMailedCardArtworkPage'));
const AdminGiftsPage = lazy(() => import('./pages/admin/AdminGiftsPage'));
const AdminGiftCategoriesPage = lazy(() => import('./pages/admin/AdminGiftCategoriesPage'));
const AdminFlowerOrdersPage = lazy(() => import('./pages/admin/AdminFlowerOrdersPage'));
const AdminMailedCardOrdersPage = lazy(() => import('./pages/admin/AdminMailedCardOrdersPage'));
const AdminGiftOrdersPage = lazy(() => import('./pages/admin/AdminGiftOrdersPage'));
const AdminSupportTicketsPage = lazy(() => import('./pages/admin/AdminSupportTicketsPage').then(m => ({ default: m.AdminSupportTicketsPage })));
const AdminSupportTicketDetailPage = lazy(() => import('./pages/admin/AdminSupportTicketDetailPage').then(m => ({ default: m.AdminSupportTicketDetailPage })));
const AdminNurtureEmailsPage = lazy(() => import('./pages/admin/AdminNurtureEmailsPage'));
const AdminOutboundEmailsPage = lazy(() => import('./pages/admin/AdminOutboundEmailsPage'));
const AdminGlockAppsPage = lazy(() => import('./pages/admin/AdminGlockAppsPage'));
const AdminMarketingPoasPage = lazy(() => import('./pages/admin/AdminMarketingPoasPage'));
const AdminAssistantHostsPage = lazy(() => import('./pages/admin/AdminAssistantHostsPage'));

/**
 * Uppercase sidebar section labels — visual grouping only (not routes).
 * Centralized so copy changes happen in one place.
 */
const APP_NAV_GROUP_LABELS = {
  peopleAndEvents: 'PEOPLE & EVENTS',
  send: 'SEND',
  activity: 'ACTIVITY',
} as const;

/**
 * Main-app sidebar layout tokens (spacing, indent).
 *
 * Tuned from UX feedback: padding before the first link, extra gap after Overview,
 * tighter vertical rhythm on leaf rows, grouped links slightly indented under headings,
 * and Contact Us anchored toward the footer via a flex grow spacer.
 */
const APP_NAV_LAYOUT = {
  /** Top padding inside the scrollable nav area before Overview. */
  sectionTopPadding: 'var(--mantine-spacing-lg)',

  /** Space between Overview and the first uppercase group heading. */
  overviewToFirstGroupGap: 'var(--mantine-spacing-xl)',

  /** Vertical gap between stacked section groups (after the first). */
  betweenGroupsSpacing: 'var(--mantine-spacing-md)',

  /** Left indent for leaves under a group heading (~20px; half of original 40px rail). */
  groupItemsIndentPx: 20,
} as const;

/**
 * Single leaf row in the main-app sidebar (path + visible label + icon).
 */
type AppNavLeafItem = {
  path: string;
  label: string;
  icon: typeof IconHome;
};

/**
 * Main-app sidebar navigation data.
 *
 * Business rules / rationale:
 * - Overview is the authenticated landing dashboard — it stays ungrouped at the top
 *   so users always see one obvious "home" without a redundant section heading.
 * - PEOPLE & EVENTS: data the user maintains (contacts, event-type definitions).
 * - SEND: products the user can send (E-Cards; Flowers only when the catalog has items).
 * - ACTIVITY: timeline-style views (upcoming vs scheduled queue).
 * - Contact Us stays ungrouped at the end of the scrollable nav list (above the
 *   pinned email / Logout footer) so the unread-reply badge stays noticeable.
 */
const overviewNavItem: AppNavLeafItem = {
  path: APP_ROUTES.overview,
  label: 'Overview',
  icon: IconHome,
};

const contactsNavItem: AppNavLeafItem = {
  path: '/contacts',
  label: 'Contacts',
  icon: IconUsers,
};

const eCardsNavItem: AppNavLeafItem = {
  path: '/templates',
  label: 'E-Cards',
  icon: IconMail,
};

const flowersNavItem: AppNavLeafItem = {
  path: APP_ROUTES.flowers,
  label: 'Flowers',
  icon: IconFlower,
};

const mailedCardsNavItem: AppNavLeafItem = {
  path: APP_ROUTES.mailedCards,
  label: 'Mailed Cards',
  icon: IconMailbox,
};

const eventTypesNavItem: AppNavLeafItem = {
  path: '/event-types',
  label: 'Event Types',
  icon: IconCalendarEvent,
};

const upcomingEventsNavItem: AppNavLeafItem = {
  path: APP_ROUTES.upcomingEvents,
  label: 'Upcoming Events',
  icon: IconCalendar,
};

const scheduledItemsNavItem: AppNavLeafItem = {
  path: '/scheduled-items',
  label: 'Scheduled Items',
  icon: IconMailFast,
};

const ordersNavItem: AppNavLeafItem = {
  path: APP_ROUTES.orders,
  label: 'Orders',
  icon: IconReceipt,
};

const contactUsNavItem: AppNavLeafItem = {
  path: APP_ROUTES.contactUs,
  label: 'Contact Us',
  icon: IconLifebuoy,
};

type AppNavGroup = {
  label: string;
  items: AppNavLeafItem[];
};

/**
 * Main application content with routing and layout
 */
const AppContent = () => {
  // Actively removes password-manager in-field icons (LastPass, 1Password)
  // via MutationObserver. CSS-only suppression is unreliable across LP versions.
  // It must never remove LastPass's root container: the passkey dialog renders
  // into it (PBI #1332).
  usePasswordManagerSuppressor();

  const { isAuthenticated, isLoading, logout, user, termsAccepted, isAdmin } = useAuth();
  const { hasFlowerProducts, hasMailedCardArtworks } = useFeatures();
  const {
    requiresWelcomePlanWall,
    isSubscribed,
    subscription,
  } = useSubscription();

  // When welcome / campaign-claim onboarding is active, omit AppShell so its
  // "Welcome back" heading cannot win e2e Promise.any races under the overlay
  // and leave requiresOnboarding true (blocking later Schedule Card modals).
  const suppressAppShell = shouldSuppressAppShellForOnboarding(
    requiresWelcomePlanWall,
    isSubscribed,
    subscription,
  );

  // Cookie-consent state is read here (not in AppFooter) so the Consent Mode v2
  // update push fires for unauthenticated visitors too. Public landing pages
  // never render AppFooter, so wiring consent there silently dropped GTM
  // bootstrap and consent updates for every ad-click visitor — the exact
  // place we most need conversion tracking.
  const { consent, isConsentSet } = useConsent();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure();

  // Unread support tickets — drives the "Contact Us" nav badge so users
  // notice when the support team has replied. Hook itself short-circuits
  // when the user is unauthenticated so we can call it unconditionally.
  const { count: supportUnreadCount } = useMySupportUnreadCount(getMySupportUnreadCount);

  // Undismissed unopened e-card warnings — badge on Scheduled Items (PBI #1160).
  // Hook gates on auth + terms internally (same as Contact Us unread badge).
  const unopenedEcardWarningCount = useUnopenedEcardWarningCount();
  /** Which legal document to show in footer modal (null = closed) */
  const [legalModalType, setLegalModalType] = useState<LegalModalType | null>(null);

  /**
   * Labeled sidebar groups (Overview and Contact Us are rendered outside this list).
   * Flowers appears in SEND only when the catalog has products.
   */
  const navGroups = useMemo<AppNavGroup[]>(
    () => [
      {
        label: APP_NAV_GROUP_LABELS.peopleAndEvents,
        items: [contactsNavItem, eventTypesNavItem],
      },
      {
        label: APP_NAV_GROUP_LABELS.send,
        items: [
          eCardsNavItem,
          ...(hasFlowerProducts ? [flowersNavItem] : []),
          ...(hasMailedCardArtworks ? [mailedCardsNavItem] : []),
        ],
      },
      {
        label: APP_NAV_GROUP_LABELS.activity,
        items: [upcomingEventsNavItem, scheduledItemsNavItem, ordersNavItem],
      },
    ],
    [hasFlowerProducts, hasMailedCardArtworks]
  );

  /**
   * Post-auth navigation for users who have completed terms.
   *
   * Ordering matters for conversion flows:
   * - If `returnTo` is set (sessionStorage), navigate there first. This preserves
   *   landing → sign-up flows that stash `/templates?scheduleTemplateId=…`, flower-shop
   *   URLs, scheduled-items deep links, etc., before redirecting to Entra.
   * - Otherwise, if the user is still on the marketing home (`/`) without an explicit
   *   “view public landing” navigation (see {@link shouldStayOnPublicLanding}), send them
   *   to Overview. That fixes “signed in but stuck on landing” and ensures guest draft
   *   review modals mount (they only render inside the authenticated AppShell). The
   *   header brand link sets `location.state` so logged-in users can re-open the
   *   marketing page when they choose to.
   *
   * IMPORTANT: This must run before any conditional returns to satisfy React hooks rules.
   */
  useEffect(() => {
    if (!isAuthenticated || !termsAccepted) {
      return;
    }

    if (hasReturnTo()) {
      const destination = getAndClearReturnTo(AUTH_ROUTES.DASHBOARD);
      navigate(destination, { replace: true });
      return;
    }

    // Preserve seasonal nurture query/hash when bouncing authed users off `/`
    // onto Overview (e.g. `/overview?eventType=EVT-005#send-a-smile`).
    if (location.pathname === AUTH_ROUTES.LANDING && !shouldStayOnPublicLanding(location.state)) {
      const search = location.search;
      let hash = location.hash;
      if (hash === `#${LANDING_EXPLORE_SECTION_ID}`) {
        hash = `#${OVERVIEW_SEND_A_SMILE_SECTION_ID}`;
      }

      navigate({ pathname: APP_ROUTES.overview, search, hash }, { replace: true });
    }
  }, [
    isAuthenticated,
    termsAccepted,
    navigate,
    location.pathname,
    location.search,
    location.hash,
    location.state,
  ]);

  // GTM + Consent Mode v2 defaults load from index.html (vite-plugin-gtm) on
  // production builds; see marketingTracking.ts and plugins/vite-plugin-gtm.ts.

  // Push a Consent Mode v2 update whenever the user accepts, declines, or
  // changes preferences. Skipped until the user has actually made a choice
  // so we do not redundantly push the default-denied state HTML already
  // seeded before gtm.js.
  useEffect(() => {
    syncAcquisitionAttributionWithConsent(consent, isConsentSet);
    if (isConsentSet) {
      updateConsentSignals(consent);
    }
  }, [consent, isConsentSet]);

  // SPA page-view tracking — emits a virtual pageview on every route change
  // so GA4 (and any other GTM destination) sees client-side navigations.
  // Uses pathname + search so query-driven views (?subscription=success) are
  // distinguishable. The helper no-ops when GTM is not configured.
  useEffect(() => {
    // SPA navigations with ad query params (e.g. in-app links) — first-touch only.
    captureAcquisitionAttributionFromLocation({
      search: location.search,
      pathname: location.pathname,
    });
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Guest draft detection: after sign-up + terms acceptance, check if the user
  // had a guest scheduling draft saved before they signed up. If so, show the
  // ReviewDraftModal for them to confirm and finalize their card scheduling.
  // Card draft is shown first; flower draft appears after card draft is cleared.
  const [guestDraft, setGuestDraft] = useState<GuestSchedulingDraft | null>(null);
  const [reviewDraftOpened, setReviewDraftOpened] = useState(false);

  const [guestFlowerDraft, setGuestFlowerDraft] = useState<GuestFlowerDraft | null>(null);
  const [reviewFlowerDraftOpened, setReviewFlowerDraftOpened] = useState(false);
  const [guestMailedCardDraft, setGuestMailedCardDraft] = useState<GuestMailedCardDraft | null>(null);
  const [reviewMailedCardDraftOpened, setReviewMailedCardDraftOpened] = useState(false);

  useEffect(() => {
    if (isAuthenticated && termsAccepted && hasGuestDraft()) {
      const draft = loadGuestDraft();
      if (draft) {
        setGuestDraft(draft);
        setReviewDraftOpened(true);
      }
    }
  }, [isAuthenticated, termsAccepted]);

  // Flower draft: detect after auth, but only show after card draft is cleared.
  // If no card draft exists, shows immediately.
  useEffect(() => {
    if (!isAuthenticated || !termsAccepted) return;

    // Wait until card draft is handled (either doesn't exist or was cleared)
    if (guestDraft) return;

    if (hasGuestFlowerDraft()) {
      const draft = loadGuestFlowerDraft();
      if (draft) {
        setGuestFlowerDraft(draft);
        setReviewFlowerDraftOpened(true);
      }
    }
  }, [isAuthenticated, termsAccepted, guestDraft]);

  useEffect(() => {
    if (!isAuthenticated || !termsAccepted) return;
    if (guestDraft || guestFlowerDraft) return;
    if (hasGuestMailedCardDraft()) {
      const draft = loadGuestMailedCardDraft();
      if (draft) {
        setGuestMailedCardDraft(draft);
        setReviewMailedCardDraftOpened(true);
      }
    }
  }, [isAuthenticated, termsAccepted, guestDraft, guestFlowerDraft]);

  // Check if the current route is public (no auth required).
  // Uses shared isPublicPath utility from auth constants.
  const isPublicRoute = isPublicPath(location.pathname);

  /**
   * Admin portal routes - have their own layout (AdminAppShell).
   */
  const isAdminRoute = location.pathname.startsWith(APP_ROUTES.adminRoot);

  // Show loading spinner while checking authentication state
  // Skip loading state for public routes
  if (isLoading && !isPublicRoute) {
    return (
      <Center style={{ height: 'var(--app-vh, 100vh)', background: semanticColors.bg.warmGradient }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // Public routes - accessible with or without authentication
  // These have standalone layouts (no AppShell)
  if (isPublicRoute) {
    return (
      <Suspense fallback={<RouteChunkFallback />}>
        <>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path={PUBLIC_ROUTES.guides} element={<GuidesIndexPage />} />
          <Route path="/guides/automated-occasion-gifts" element={<AutomatedOccasionGiftsPage />} />
          <Route path="/guides/*" element={<PublicNotFoundPage />} />
          <Route path="/ai" element={<PublicAiPage />} />
          <Route path="/ai/use-cases/:slug" element={<AiUseCaseDocPage />} />
          <Route path="/cards/:slug" element={<PublicEcardProductPage />} />
          <Route path="/cards" element={<CardsPage />} />
          <Route path="/flowers/:publicId" element={<PublicFlowerProductPage />} />
          <Route path="/flowers" element={<PublicFlowersPage />} />
          <Route path="/mailed-cards/:slug" element={<PublicMailedCardProductPage />} />
          <Route path="/mailed-cards" element={<PublicMailedCardsPage />} />
          <Route path={PUBLIC_ROUTES.gifts} element={<GiftShopRoute><PublicGiftsPage /></GiftShopRoute>} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/autopilot" element={<IntroOfferPage />} />
          <Route path="/never-miss" element={<NeverMissPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/reminders/confirm" element={<ReminderConfirmPage />} />
          <Route path="/reminders/unsubscribe" element={<ReminderUnsubscribePage />} />
          <Route path="/account/marketing-unsubscribe" element={<MarketingUnsubscribePage />} />
          <Route path="/help/email-delivery" element={<EmailWhitelistHelpPage />} />
          <Route path="/card/preview" element={<CardPreviewPage />} />
          <Route path="/card/:viewToken" element={<CardViewPage />} />
          <Route path="/agent/ecard-review" element={<AgentEcardReviewPage />} />
          <Route path="/agent/contact-change-review" element={<AgentContactChangeReviewPage />} />
          <Route path="/agent/order-review" element={<AgentOrderReviewPage />} />
          {import.meta.env.MODE === 'e2e' && PhoneE2eHarnessPage ? (
            <Route path="/__e2e/phone" element={<PhoneE2eHarnessPage />} />
          ) : null}
          <Route path="*" element={<PublicNotFoundPage />} />
        </Routes>
        {/* Bug #1388: banner mounts only after lazy public route commits */}
        <DeferredPublicCookieBanner />
        </>
      </Suspense>
    );
  }

  // Unauthenticated routes - IMPORTANT: We still register protected routes here
  // so deep links like /scheduled-items?edit=19 are matched and handled by ProtectedRoute
  // (which will store returnTo and redirect to /auth/signin)
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<RouteChunkFallback />}>
        <Routes>
          {/* Public pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path={PUBLIC_ROUTES.guides} element={<GuidesIndexPage />} />
          <Route path="/guides/automated-occasion-gifts" element={<AutomatedOccasionGiftsPage />} />
          <Route path="/guides/*" element={<PublicNotFoundPage />} />
          <Route path="/ai" element={<PublicAiPage />} />
          <Route path="/ai/use-cases/:slug" element={<AiUseCaseDocPage />} />
          <Route path="/cards/:slug" element={<PublicEcardProductPage />} />
          <Route path="/cards" element={<CardsPage />} />
          <Route path="/flowers/:publicId" element={<PublicFlowerProductPage />} />
          <Route path="/flowers" element={<PublicFlowersPage />} />
          <Route path="/mailed-cards/:slug" element={<PublicMailedCardProductPage />} />
          <Route path="/mailed-cards" element={<PublicMailedCardsPage />} />
          <Route path={PUBLIC_ROUTES.gifts} element={<GiftShopRoute><PublicGiftsPage /></GiftShopRoute>} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/autopilot" element={<IntroOfferPage />} />
          <Route path="/never-miss" element={<NeverMissPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/reminders/confirm" element={<ReminderConfirmPage />} />
          <Route path="/reminders/unsubscribe" element={<ReminderUnsubscribePage />} />
          <Route path="/account/marketing-unsubscribe" element={<MarketingUnsubscribePage />} />
          <Route path="/help/email-delivery" element={<EmailWhitelistHelpPage />} />
          <Route path="/agent/ecard-review" element={<AgentEcardReviewPage />} />
          <Route path="/agent/contact-change-review" element={<AgentContactChangeReviewPage />} />
          <Route path="/agent/order-review" element={<AgentOrderReviewPage />} />

          {/* Auth flow pages */}
          <Route path={AUTH_ROUTES.SIGNIN} element={<AuthStartPage flow="signin" />} />
          <Route path={AUTH_ROUTES.SIGNUP} element={<AuthStartPage flow="signup" />} />
          <Route path={AUTH_ROUTES.CALLBACK} element={<AuthCallbackPage />} />
          <Route path={AUTH_ROUTES.SILENT_CALLBACK} element={<AuthCallbackPage />} />

          {/* Legacy compatibility - auto-redirect to new auth start */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected routes - ProtectedRoute will handle redirect to /auth/signin with returnTo */}
          <Route path={APP_ROUTES.overview} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.legacyDashboard} element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
          <Route path="/event-types" element={<ProtectedRoute><EventTypesPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.upcomingEvents} element={<ProtectedRoute><UpcomingEventsPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.orders} element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/scheduled-items" element={<ProtectedRoute><ScheduledItemsPage /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.settings} element={<ProtectedRoute><MyAccountPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.flowers} element={<ProtectedRoute><FlowersPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.mailedCards} element={<ProtectedRoute><MailedCardsPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.connectedAssistants} element={<ProtectedRoute><ConnectedAssistantsPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.contactUs} element={<ProtectedRoute><ContactUsPage /></ProtectedRoute>} />
          <Route path={APP_ROUTES.contactUsThread} element={<ProtectedRoute><ContactUsThreadPage /></ProtectedRoute>} />

          {/* Catch-all: protected paths → sign-in; everything else → public 404 */}
          <Route path="*" element={<UnauthenticatedCatchAll />} />
        </Routes>
      </Suspense>
    );
  }

  // Authenticated but hasn't accepted terms - must accept before proceeding
  // Allow access to /accept-terms, /terms, /privacy, and auth callback routes
  // Note: We don't clear returnTo here - AcceptTermsPage will use it after acceptance
  if (!termsAccepted) {
    const isTermsRelatedRoute =
      location.pathname === AUTH_ROUTES.ACCEPT_TERMS ||
      location.pathname === '/terms' ||
      location.pathname === '/privacy' ||
      location.pathname.startsWith('/auth/');

    if (!isTermsRelatedRoute) {
      return <Navigate to={AUTH_ROUTES.ACCEPT_TERMS} replace />;
    }

    // Render terms-related routes for authenticated users who haven't accepted
    return (
      <Suspense fallback={<RouteChunkFallback />}>
        <Routes>
          <Route path={AUTH_ROUTES.ACCEPT_TERMS} element={<AcceptTermsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path={AUTH_ROUTES.CALLBACK} element={<Navigate to={AUTH_ROUTES.ACCEPT_TERMS} replace />} />
          <Route path={AUTH_ROUTES.SILENT_CALLBACK} element={<Navigate to={AUTH_ROUTES.ACCEPT_TERMS} replace />} />
          <Route path="*" element={<Navigate to={AUTH_ROUTES.ACCEPT_TERMS} replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Admin portal routes - separate layout from main app
  if (isAdminRoute) {
    return (
      <AdminAppShell>
        <Suspense fallback={<Center style={{ height: '50vh' }}><Loader size="lg" /></Center>}>
          <Routes>
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminCustomersPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminOperations}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminOperationsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/audit-events"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminAuditEventsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminTemplatesPage />
                </AdminRoute>
              }
            />
            <Route path="/admin/carousel" element={<Navigate to="/admin/templates" replace />} />
            <Route
              path="/admin/event-types"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminEventTypesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/site-settings"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminSiteSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/placeholders"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminPlaceholdersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/ai-models"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminAiModelsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/content-generation"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminContentGenerationPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/flowers/categories"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminFlowerCategoriesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/flower-vendors"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminFlowerVendorsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminContactRelationships}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminContactRelationshipsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminFlowerPromotions}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminFlowerPromotionsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminGiftCategories}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminGiftCategoriesPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminGifts}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminGiftsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/mailed-cards"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminMailedCardArtworkPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/flower-orders"
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminFlowerOrdersPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminMailedCardOrders}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminMailedCardOrdersPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminGiftOrders}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminGiftOrdersPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminSupportTickets}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminSupportTicketsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminSupportTicketDetail}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminSupportTicketDetailPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminNurtureEmails}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'ContentManager']}>
                  <AdminNurtureEmailsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminEmailStats}
              element={
                <AdminRoute requiredRoles={['SuperAdmin', 'SupportAgent']}>
                  <AdminOutboundEmailsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminGlockApps}
              element={
                <AdminRoute requiredRoles={['SuperAdmin']}>
                  <AdminGlockAppsPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminMarketingPoas}
              element={
                <AdminRoute requiredRoles={['SuperAdmin']}>
                  <AdminMarketingPoasPage />
                </AdminRoute>
              }
            />
            <Route
              path={APP_ROUTES.adminAssistantHosts}
              element={
                <AdminRoute requiredRoles={['SuperAdmin']}>
                  <AdminAssistantHostsPage />
                </AdminRoute>
              }
            />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Suspense>
      </AdminAppShell>
    );
  }

  /**
   * Handles navigation and closes mobile menu
   */
  const handleNavigation = (path: string) => {
    navigate(path);
    close();
  };

  /**
   * Renders one main-app sidebar row with shared styling and urgency badges.
   */
  const renderNavLink = (item: AppNavLeafItem) => {
    // The Contact Us nav row gets a small badge whenever the user
    // has unread admin replies in their support inbox.
    const showSupportBadge =
      item.path === APP_ROUTES.contactUs && supportUnreadCount > 0;

    // Scheduled Items shows undismissed unopened e-card warning count (PBI #1160).
    const showUnopenedBadge =
      item.path === scheduledItemsNavItem.path && unopenedEcardWarningCount > 0;

    const badgeCount = showSupportBadge
      ? supportUnreadCount
      : showUnopenedBadge
        ? unopenedEcardWarningCount
        : 0;

    const badgeColor = showSupportBadge ? brandColors.primary : brandColors.warning;

    return (
      <NavLink
        key={item.path}
        label={item.label}
        leftSection={<item.icon size={20} />}
        rightSection={
          badgeCount > 0 ? (
            <Badge
              color={badgeColor}
              variant="filled"
              size="xs"
              data-testid={showUnopenedBadge ? 'nav-scheduled-items-unopened-badge' : undefined}
            >
              {badgeCount}
            </Badge>
          ) : undefined
        }
        active={location.pathname === item.path}
        onClick={() => handleNavigation(item.path)}
        style={{ borderRadius: rem(8) }}
        styles={(theme, { active }) => ({
          root: {
            marginBottom: rem(2),
            paddingBlock: rem(6),
          },
          label: {
            color: active ? undefined : theme.colors.gray[9],
            fontWeight: active ? undefined : 500,
          },
          section: {
            color: active ? undefined : theme.colors.gray[9],
          },
        })}
      />
    );
  };

  // Subscription gates (after terms, before normal app):
  // 1. Welcome onboarding: shown on first login for plan selection
  // 2. Contact selection: blocking modal when lapsed user has >limit contacts
  // WelcomeOnboarding returns null when not needed; when active it renders full-screen.
  // AppShell is suppressed while onboarding is required so Overview cannot race
  // under the overlay (see welcomeOnboardingGate.ts).
  // ContactSelectionModal is a fullscreen modal overlay that cannot be dismissed.

  // Authenticated routes with app shell layout
  return (
    <>
    <Suspense fallback={null}>
      <WelcomeOnboarding />
    </Suspense>
    {!suppressAppShell && (
    <>
    <Suspense fallback={null}>
      <ContactSelectionModal />
    </Suspense>
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 260,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
      styles={{
        main: {
          background: semanticColors.bg.warmGradient,
          minHeight: 'var(--app-vh, 100vh)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Notifications position="top-right" pauseResetOnHover="notification" />

      {/* Guest card draft review modal — auto-popup after sign-up when a card draft exists */}
      {guestDraft && (
        <Suspense fallback={null}>
          <ReviewDraftModal
            draft={guestDraft}
            opened={reviewDraftOpened}
            onClose={() => setReviewDraftOpened(false)}
            onDraftCleared={() => {
              setGuestDraft(null);
              setReviewDraftOpened(false);
            }}
          />
        </Suspense>
      )}

      {/* Guest flower draft review modal — shown after card draft is handled */}
      {guestFlowerDraft && (
        <Suspense fallback={null}>
          <ReviewFlowerDraftModal
            draft={guestFlowerDraft}
            opened={reviewFlowerDraftOpened}
            onClose={() => setReviewFlowerDraftOpened(false)}
            onDraftCleared={() => {
              setGuestFlowerDraft(null);
              setReviewFlowerDraftOpened(false);
            }}
          />
        </Suspense>
      )}

      {guestMailedCardDraft && (
        <Suspense fallback={null}>
          <ReviewMailedCardDraftModal
            draft={guestMailedCardDraft}
            opened={reviewMailedCardDraftOpened}
            onClose={() => setReviewMailedCardDraftOpened(false)}
            onDraftCleared={() => {
              setGuestMailedCardDraft(null);
              setReviewMailedCardDraftOpened(false);
            }}
          />
        </Suspense>
      )}

      {/* Header with logo and mobile burger */}
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation"
            />
            <Link
              to={AUTH_ROUTES.LANDING}
              state={{ [VIEW_PUBLIC_LANDING_STATE_KEY]: true }}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--mantine-spacing-xs)',
              }}
            >
              <Box
                component="img"
                src="/logo-icon.svg"
                alt="Scheduled Smiles"
                style={{ width: logoSizes.small, height: logoSizes.small }}
              />
              <Title order={3} visibleFrom="xs">Scheduled Smiles</Title>
            </Link>
          </Group>
          {/* User menu dropdown with avatar */}
          <UserAccountMenu
            user={user}
            navigate={navigate}
            logout={logout}
            leadingItems={
              isAdmin ? (
                <>
                  <Menu.Item
                    leftSection={<IconShield size={14} />}
                    onClick={() => navigate(APP_ROUTES.adminRoot)}
                    color={brandColors.admin}
                    className={portalSwitchMenuItemClasses.portalSwitchItem}
                  >
                    Admin Portal
                  </Menu.Item>
                  <Menu.Divider />
                </>
              ) : undefined
            }
          />
        </Group>
      </AppShell.Header>

      {/* Navigation sidebar */}
      <AppShell.Navbar p="md">
        {/*
          grow + ScrollArea: nav links scroll when zoom or short viewports shrink
          effective height; email + Logout stay pinned (#1558, AdminAppShell parity).
        */}
        <AppShell.Section grow component={ScrollArea} type="hover" offsetScrollbars>
          <Box style={{ paddingTop: APP_NAV_LAYOUT.sectionTopPadding }}>
            {renderNavLink(overviewNavItem)}
            {navGroups.map((group, groupIndex) => (
              <Box key={group.label}>
                <Text
                  role="presentation"
                  size="xs"
                  fw={700}
                  tt="uppercase"
                  c={semanticColors.text.heading}
                  px="xs"
                  mt={
                    groupIndex === 0
                      ? APP_NAV_LAYOUT.overviewToFirstGroupGap
                      : APP_NAV_LAYOUT.betweenGroupsSpacing
                  }
                  mb={rem(4)}
                >
                  {group.label}
                </Text>
                <Stack gap={0} pl={rem(APP_NAV_LAYOUT.groupItemsIndentPx)}>
                  {group.items.map(renderNavLink)}
                </Stack>
              </Box>
            ))}
          </Box>

          <Box pt="sm">{renderNavLink(contactUsNavItem)}</Box>
        </AppShell.Section>

        <AppShell.Section>
          <Divider my="sm" />
          <Text size="xs" c={semanticColors.text.secondary} ta="center" mb="xs">
            {user?.email}
          </Text>
          <Button
            variant="light"
            color={brandColors.neutral}
            fullWidth
            leftSection={<IconLogout size={16} />}
            onClick={logout}
          >
            Logout
          </Button>
        </AppShell.Section>
      </AppShell.Navbar>

      {/* Main content area - flex column so footer stays at bottom on short pages */}
      <AppShell.Main>
        {/* Content wrapper grows to fill space; viewport handles scrolling */}
        <Box style={{ flex: 1, minHeight: 0 }}>
          <Suspense fallback={<Center style={{ height: '50vh' }}><Loader size="lg" /></Center>}>
          <Routes>
            <Route
              path={APP_ROUTES.overview}
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            {/* Backward-compatible redirect for older bookmarks/links */}
            <Route path={APP_ROUTES.legacyDashboard} element={<Navigate to={APP_ROUTES.overview} replace />} />
            <Route
              path="/contacts"
              element={
                <ProtectedRoute>
                  <ContactsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event-types"
              element={
                <ProtectedRoute>
                  <EventTypesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.upcomingEvents}
              element={
                <ProtectedRoute>
                  <UpcomingEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.orders}
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scheduled-items"
              element={
                <ProtectedRoute>
                  <ScheduledItemsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.flowers}
              element={
                <ProtectedRoute>
                  <FlowersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.mailedCards}
              element={
                <ProtectedRoute>
                  <MailedCardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.connectedAssistants}
              element={
                <ProtectedRoute>
                  <ConnectedAssistantsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <MyAccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.settings}
              element={
                <ProtectedRoute>
                  <MyAccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.contactUs}
              element={
                <ProtectedRoute>
                  <ContactUsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={APP_ROUTES.contactUsThread}
              element={
                <ProtectedRoute>
                  <ContactUsThreadPage />
                </ProtectedRoute>
              }
            />
            {/* Auth routes (in case user is already authenticated) */}
            <Route path={AUTH_ROUTES.SIGNIN} element={<AuthStartPage flow="signin" />} />
            <Route path={AUTH_ROUTES.SIGNUP} element={<AuthStartPage flow="signup" />} />
            {/* Use AuthCallbackPage for callback to respect returnTo */}
            <Route path={AUTH_ROUTES.CALLBACK} element={<AuthCallbackPage />} />
            <Route path={AUTH_ROUTES.SILENT_CALLBACK} element={<AuthCallbackPage />} />
            {/*
              Safety net: when termsAccepted transitions from false → true, React may
              briefly render the authenticated layout while the location is still
              /accept-terms. Without this route, the catch-all would redirect to
              /overview and lose any pending returnTo URL. AcceptTermsPage handles
              its own navigation, so rendering it here is harmless.
            */}
            <Route path={AUTH_ROUTES.ACCEPT_TERMS} element={<AcceptTermsPage />} />
            <Route path="*" element={<AppNotFoundPage />} />
          </Routes>
          </Suspense>
        </Box>

        {/* Footer at bottom of main - does not shrink; stays at bottom when content is short */}
        <AppFooter setLegalModalType={setLegalModalType} />
      </AppShell.Main>
      <LegalModal
        type={legalModalType}
        opened={legalModalType !== null}
        onClose={() => setLegalModalType(null)}
      />
    </AppShell>
    </>
    )}
    </>
  );
};

/**
 * App footer with legal links and cookie consent controls.
 * Separated to use the useConsent hook which requires ConsentProvider.
 *
 * NOTE: GTM bootstrap and Consent Mode v2 update wiring used to live here
 * but moved to AppContent so they fire on public landing pages too (this
 * footer only renders inside the authenticated AppShell layout).
 */
const AppFooter: React.FC<{ setLegalModalType: (type: LegalModalType | null) => void }> = ({
  setLegalModalType,
}) => {
  const { openPreferences } = useConsent();

  return (
    <Stack align="center" gap="xs" mt="xl" mb="md" style={{ flexShrink: 0 }}>
      {/* Legal and privacy links - single row */}
      <Group gap="md" wrap="wrap" justify="center">
        <UnstyledButton
          component="button"
          type="button"
          onClick={() => setLegalModalType('terms')}
          style={{ cursor: 'pointer' }}
        >
          <Text size="xs" c={semanticColors.text.secondary} td="underline">
            Terms and Conditions
          </Text>
        </UnstyledButton>
        <Text size="xs" c={semanticColors.text.secondary}>•</Text>
        <UnstyledButton
          component="button"
          type="button"
          onClick={() => setLegalModalType('privacy')}
          style={{ cursor: 'pointer' }}
        >
          <Text size="xs" c={semanticColors.text.secondary} td="underline">
            Privacy Policy
          </Text>
        </UnstyledButton>
        <Text size="xs" c={semanticColors.text.secondary}>•</Text>
        <UnstyledButton
          component="button"
          type="button"
          onClick={openPreferences}
          style={{ cursor: 'pointer' }}
        >
          <Text size="xs" c={semanticColors.text.secondary} td="underline">
            {CONSENT_UI_TEXT.privacyChoicesLink}
          </Text>
        </UnstyledButton>
      </Group>

      <Text size="xs" c={semanticColors.text.secondary}>
        © {new Date().getFullYear()} Scheduled Smiles
      </Text>
    </Stack>
  );
};

/**
 * Root App Component
 * Provides MantineProvider, ConsentProvider, AuthProvider, and BrowserRouter
 *
 * Provider hierarchy:
 * - MantineProvider: Mantine theme and styles
 * - BrowserRouter: React Router (+ ScrollToTop for window scroll reset)
 * - ConsentProviderWithModal: Cookie consent state and modal control
 * - AuthProvider: Authentication state
 * - UpgradeModalProvider: Global 402 upgrade modal (inside SubscriptionProvider)
 * - PaymentMethodModalProvider: Global 402 card-on-file modal (PBI #1181)
 * - AppContent: Main application routes and layout
 */
const App = () => {
  return (
    <MantineProvider theme={appTheme} cssVariablesResolver={appCssVariablesResolver}>
      <BrowserRouter>
        {/* Reset window scroll on route change — catalog infinite-scroll pages
            leave the document scrolled down; public landing must open at top. */}
        <ScrollToTop />
        <ConsentProviderWithModal>
          <AuthProvider>
            <PlaceholderProvider>
            <FeaturesProvider>
              <SubscriptionProvider>
                <UpgradeModalProvider>
                  <PaymentMethodModalProvider>
                    <AppContent />
                    {/* Cookie banner: public paths use DeferredPublicCookieBanner inside Suspense */}
                    <AppRootCookieBanner />
                    <CookiePreferencesModal />
                    {/*
                      Shared aria-live region for form validation. The
                      useScrollToFirstError hook writes the count of errored
                      fields here so screen readers announce "N field(s) need
                      attention" whenever a Save attempt fails validation.
                      Mounted once at the root so every dialog and form gets
                      the same target without each one having to render its own.
                    */}
                    <span
                      id="ss-form-error-live"
                      aria-live="polite"
                      aria-atomic="true"
                      className="visually-hidden"
                    />
                  </PaymentMethodModalProvider>
                </UpgradeModalProvider>
              </SubscriptionProvider>
            </FeaturesProvider>
            </PlaceholderProvider>
          </AuthProvider>
        </ConsentProviderWithModal>
      </BrowserRouter>
    </MantineProvider>
  );
};

export default App;
